/**
 * Product Option Combined Component
 */
import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { type FunctionComponent, type HTMLAttributes } from 'react';
import { type ProductOptionGroupFieldsFragment, type ProductOptionFieldsFragment } from '../../../__generated__/graphql-client-types';
import { changeOption } from '../../../helpers/product-helper/configuration/product-change-option.helper';
import { isCurrentOptionSelected } from '../../../helpers/product-helper/configuration/product-option.helper';
import { type GroupSelection, type OptionSelection, type ProductOptionSelection } from '../../../types/product-option.types';
import { ProductFactoryOptionsList } from '../product-option/product-option.component';
import { ProductCardActions } from '../product-option-card/product-card-actions/product-card-actions.component';
import { ProductSelectionCard } from '../product-option-card/product-selection-card.component';

export type ProductOptionCombinedProps = {
	selections: OptionSelection[];
	groupPosition: number;
} & ProductOptionGroupFieldsFragment &
	Pick<ProductOptionSelection, 'update'> &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductOptionCombined: FunctionComponent<ProductOptionCombinedProps> = ({ className = '', selections, update, ...group }) => {
	const selectedOption = group.options.find((option) => isCurrentOptionSelected(option, selections));
	const { resource } = selectedOption || {};
	const selectedVariant = resource ? resource.finishes.find((finish) => finish.id === resource.variantId) : null;
	const onOptionChange = (option: ProductOptionFieldsFragment, groupSelection?: Pick<GroupSelection, 'id' | 'valid'>): void => {
		const optionSelection = selections.find((currentOption) => currentOption.id === kebabCase(option.name));
		return update && optionSelection && update(changeOption(option, selections), optionSelection, groupSelection);
	};
	return (
		<>
			<ProductFactoryOptionsList className={className} selections={selections} change={onOptionChange} {...group} />
			{selectedOption && selectedVariant && (
				<section className="flex flex-column items-start bl b--theme-black mt2 pl3">
					<ProductSelectionCard
						{...selectedOption}
						ElementTag="div"
						testId={kebabCase(group.label)}
						automationHook="product-option-selected"
						variant={selectedVariant}>
						<ProductCardActions
							className="items-center justify-end mt3"
							link={{
								name: 'View Full Product Details',
								url: selectedVariant.url,
								automationHook: 'product-option-see-more-details',
								openInNewTab: true
							}}
						/>
					</ProductSelectionCard>
				</section>
			)}
		</>
	);
};
